/**
 * Ride Fare Estimate Component
 * Isolated ride component - does not touch package system
 */
import { DollarSign, Route, Clock, Zap } from 'lucide-react';

const RideFareEstimate = ({
  onRetry = null, 
  estimate, 
  loading = false, 
  error = null,
  currency = 'JMD'
}) => {
  if (loading) {
    return (
      <div className="p-6 bg-gray-50 rounded-xl animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 rounded-xl border border-red-200">
        <p className="text-red-600 text-sm">{error}</p>
        {onRetry && (
          <button onClick={onRetry} className="mt-2 text-sm text-red-700 underline hover:text-red-800">
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="p-6 bg-gray-50 rounded-xl border border-dashed border-gray-300 text-center">
        <DollarSign className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">Enter pickup and destination to see fare estimate</p>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    if (currency === 'USD') {
      return `$${(Number(amount) || 0).toFixed(2)} USD`;
    }
    return `J$${(Number(amount) || 0).toLocaleString()}`;
  };

  const { breakdown = {} } = estimate;

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-200 overflow-hidden" data-testid="fare-estimate">
      {/* Main fare */}
      <div className="p-6 text-center border-b border-emerald-100">
        <p className="text-sm text-gray-500 mb-1">Estimated Fare</p>
        <p className="text-4xl font-bold text-emerald-600">
          {formatCurrency(estimate.estimated_fare || estimate.fare_jmd)}
        </p>
        {estimate.surge_active && (
          <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
            <Zap className="w-4 h-4" />
            Surge {estimate.surge_multiplier}x active
          </div>
        )}
      </div>

      {/* Breakdown */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 flex items-center gap-2">
            <Route className="w-4 h-4" />
            Distance
          </span>
          <span className="font-medium">{(Number(estimate.distance_km) || 0).toFixed(1)} km</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Est. Duration
          </span>
          <span className="font-medium">{estimate.duration_min} min</span>
        </div>

        {breakdown.base_fare && (
          <>
            <hr className="border-gray-200" />
            <div className="space-y-2 text-xs text-gray-500">
              <div className="flex justify-between">
                <span>Base fare</span>
                <span>J${breakdown.base_fare?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Distance ({(Number(estimate.distance_km) || 0).toFixed(1)} km)</span>
                <span>J${breakdown.distance_charge?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Time ({estimate.duration_min} min)</span>
                <span>J${breakdown.time_charge?.toLocaleString()}</span>
              </div>
              {breakdown.surge_multiplier > 1 && (
                <div className="flex justify-between text-orange-600">
                  <span>Surge ({breakdown.surge_multiplier}x)</span>
                  <span>Applied</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer note */}
      <div className="px-4 py-3 bg-emerald-50 text-xs text-emerald-700 text-center">
        Final fare may vary based on actual route and traffic
      </div>
    </div>
  );
};

export default RideFareEstimate;
